/* eslint-disable @typescript-eslint/no-invalid-void-type */
/* eslint-disable @typescript-eslint/prefer-nullish-coalescing */
import { type FC, useEffect, useState } from 'react';
import { type registration } from '@/api';
import ImageSelectField from '@/base/Form/ImageSelectField';
import { type FormikErrors, useField } from 'formik';
import { useDebounce } from 'use-debounce';
import { IsRequiredType, type DiscountCode } from '@/api/registration';
import BaseFormInputField from '@/base/Form/InputField';
import { UiStack, UiText } from '@/lib/ui';
import { type ApiResponse } from '@/api/tenantClient';
import { useEventSettingsQuery } from '@/registration/hook/useEventSettingsQuery';
import { useRegisterRoute } from '@/registration/hook/useRegisterRoute';

export interface DiscountCodeFormProps {
  existingDiscountCode: DiscountCode | undefined
  onCheckDiscountCode: (code: string) => Promise<ApiResponse<registration.DiscountCode>>
  setDisabledDiscountCode: (field: string, value: any, shouldValidate?: boolean) => Promise<void | FormikErrors<FormData>>
  disabled?: boolean
}

const DiscountCodeForm: FC<DiscountCodeFormProps> = ({ existingDiscountCode, onCheckDiscountCode, disabled, setDisabledDiscountCode }) => {
  const [error, setError] = useState<string>();
  const { eventId } = useRegisterRoute();
  const [, { value: discountCodeString }] = useField<string>('discountCodeString');
  const [debouncedDiscountCode] = useDebounce<string>(discountCodeString, 1000);
  const [, { value }, { setValue }] = useField<DiscountCode | undefined>('discountCode');
  const { data: eventSettings } = useEventSettingsQuery(eventId);

  useEffect(() => {
    void (async () => {
      await setDisabledDiscountCode('isDisabledDiscountCode', true);
      if (!debouncedDiscountCode || debouncedDiscountCode === '') {
        void setValue(undefined);
        setError(undefined);
      } else {
        const response = await onCheckDiscountCode(debouncedDiscountCode);
        if (response.errors && response.errors.length > 0) {
          void setValue(undefined);
          await setDisabledDiscountCode('isDisabledDiscountCode', true);
          setError('Invalid discount code');
        } else {
          void setValue(response.items[0]);
          await setDisabledDiscountCode('isDisabledDiscountCode', false);
          setError(undefined);
        }
      }
    })();
  }, [debouncedDiscountCode, onCheckDiscountCode, setValue]);

  useEffect(() => {
    if (existingDiscountCode) {
      void setValue(existingDiscountCode);
    }
  }, [setValue]);

  return (
    <>
      <UiStack spacing={4}>
        <BaseFormInputField
          name={'discountCodeString'}
          label={eventSettings?.pageMetadata?.attendeeCategory?.discountCodeLabel || 'Discount code'}
          isRequired={false}
          layout={'stack'}
          disabled={disabled}
        />
        {error && (<UiText color={'red.500'}>{error}</UiText>)}
      </UiStack>
      {(value?.idPhotocopy === IsRequiredType.REQUIRED || value?.idPhotocopy === IsRequiredType.OPTIONAL) && (
        <ImageSelectField
          name="idPhotocopy"
          label={eventSettings?.pageMetadata?.attendeeCategory?.idPhotocopyLabel || 'ID photocopy'}
          isRequired={value?.idPhotocopy === IsRequiredType.REQUIRED}
          layout={'stack'}
          disabled={disabled}
        />
      )}
      {(value?.idInfo === IsRequiredType.REQUIRED || value?.idInfo === IsRequiredType.OPTIONAL) && (
        <BaseFormInputField
          name={'idInfo'}
          label={eventSettings?.pageMetadata?.attendeeCategory?.idInformationLabel || 'ID Info'}
          isRequired={value?.idInfo === IsRequiredType.REQUIRED}
          layout={'stack'}
          disabled={disabled}
        />
      )}
    </>
  );
};

export default DiscountCodeForm;
